import Phaser from 'phaser';
import { speakRoundIntro, speaksIntroOn } from '@/game/roundIntro';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import { getBackground, getBestLabyrinth, setBestLabyrinth, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg, pillTexture } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { scriptFontFamily } from '@/game/fonts';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackLabyrinthStart, trackLabyrinthEnd } from '@/analytics';
import {
  playThudSound,
  playGateSound,
  playRecordSound,
  playRoundCompleteSound,
  speakRulePrompt,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import { LABYRINTH } from '@/game/config';
import { dealBoard, usableSets, type DealtSentence } from '@/game/labyrinth/deal';
import { edgeKey, type Cell, type LabyrinthBoard } from '@/game/labyrinth/maze';
import {
  startWalk, move, strip, gateCell, type WalkState,
} from '@/game/labyrinth/walk';
import type { SentenceSet } from '@/game/labyrinth/sets';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface LabyrinthInit {
  lang: string;
  /** Dev only (`?labyrinth=3`): start the ladder further up, where the chained paths live. */
  startRound?: number;
}

/**
 * The play area. There is no banner: this mode asks nothing (§4.3), which is the whole design, so
 * the top of the screen carries only the clock and the grid gets the height a word cell needs.
 */
const PLAY = { x: 80, y: 128, w: 1120, h: 432 };

/**
 * The standing instruction, on the line every lesson puts its question (BoardScene, SurvivalScene,
 * MazeScene). A child crossing from a lesson into a reward should find the rule where they already
 * look — and it is a rule, not a clue: it says what the board wants, never what is on it (§4.3).
 *
 * Shown on exactly the rounds the instruction is SPOKEN — one gate, `speaksIntroOn`, not a local
 * constant that has to agree with it. The band stays reserved either way, so the
 * grid never resizes mid-run.
 */
const BANNER_Y = 56;
const CLOCK_Y = 100;
const CLOCK_W = 420;
const CLOCK_FILL = 0xffd166;
const CLOCK_LOW = 0xff8f8f;

/** Where the strip is written — what the child has read so far, and the board's only feedback. */
const STRIP_Y = 616;

const BATTERY = '🔋';
/**
 * The maze is drawn as CORRIDORS, not as walls.
 *
 * The first build drew a wall wherever a corridor was missing, and play-testing came back with
 * "strange lines — connectors… not clear how to continue": a thin line between two cells reads as a
 * join, not as a barrier, so the board said the opposite of what it meant. Now a dark slab covers
 * the whole grid, the rooms and the passages between them are cut out of it in the room colour, and
 * a wall is simply where no passage was cut. Nothing has to be interpreted — you can walk where the
 * floor goes, which is how the paper puzzle this mode comes from reads.
 */
const SLAB = 0x232a33;
const CORRIDOR_W = 0.42;
const TILE_FILL = 0xf6efe2;
const TILE_TEXT = '#1a2430';
const TRAIL_FILL = 0xffe9a8;
const TRAIL_LINE = 0xe8912d;
/**
 * The trail once it leaves the true path. Red from the wrong turn onward, and the cell the child is
 * standing on pulses.
 *
 * The strip at the bottom already says the route has stopped making a sentence, but reading it
 * back is exactly what a child who has lost the thread cannot do — that is why they are lost. This
 * says the same thing where their finger is: the way back is the red part, and the blinking cell is
 * the end of it.
 */
const WRONG_FILL = 0xf3c0bb;
const WRONG_LINE = 0xd6453a;
/** The way out, drawn after a board is lost — the consolation, and the only teaching a lost board does. */
const REVEAL_FILL = 0xd9c2f2;
const REVEAL_TEXT = '#3b2166';

interface Tile {
  col: number;
  row: number;
  plate: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
}

/**
 * Word Labyrinth — the Sun (star 9) reward minigame (specs/labyrinth-mode.md).
 *
 * A walled maze whose cells hold whole words. Nothing is asked: the child steps in from the left,
 * every cell they touch adds its word to a strip along the bottom, and **the gate opens only for
 * the route that reads as a sentence**. Reaching the exit is not winning.
 *
 * Self-contained like SurvivalScene, PairsScene and MazeScene: it touches neither RoundScene nor
 * BoardScene nor the Leitner engine, and is entered only by spending a banked Satellite. What is on
 * the board is decided by the Phaser-free modules in game/labyrinth/; this scene only draws them
 * and turns taps into moves.
 */
export class LabyrinthScene extends Phaser.Scene {
  private lang!: string;
  private startRound = 1;

  private sets: SentenceSet[] = [];
  private usedSetIds = new Set<string>();

  private round = 1;
  private batteries = LABYRINTH.startBatteries;
  private roundsCleared = 0;
  private best = 0;

  private board: LabyrinthBoard | null = null;
  private sentence: DealtSentence | null = null;
  private state: WalkState | null = null;

  private tiles: Tile[][] = [];
  private tileTexKeys: string[] = [];
  private cellW = 120;
  private cellH = 100;
  private gridX = 0;
  private gridY = 0;

  /** The slab, the rooms and the passages (depth 2) — see the CORRIDOR note at the top. */
  private wallLayer!: Phaser.GameObjects.Graphics;
  /** The gate, alone, so opening it is one cheap redraw above the slab. */
  private gateLayer!: Phaser.GameObjects.Graphics;
  /**
   * The trail, UNDER the words rather than over them (depth 6 against the labels' 8). Drawn over
   * the top it crossed out the very words the child is reading, which is what the play-test note
   * about "strange lines" was partly seeing.
   */
  private trailLayer!: Phaser.GameObjects.Graphics;
  private gateMark: Phaser.GameObjects.Text | null = null;
  /** Entrance arrow and anything else drawn outside the slab, cleared with the board. */
  private markers: Phaser.GameObjects.Text[] = [];

  /** Pointer is down and dragging through corridors; the last cell a drag already acted on. */
  private dragging = false;
  private lastDragCell: string | null = null;

  /** The open gate's own countdown to walking the child out — see armWalkOut. */
  private gateTimer: Phaser.Time.TimerEvent | null = null;
  /** The pulse on a wrong trail's head, held so it can be stopped when the trail moves. */
  private blink: Phaser.Tweens.Tween | null = null;

  private msLeft = 0;
  private running = false;
  private over = false;

  private batteryIcons: Phaser.GameObjects.Text[] = [];
  private roundText!: Phaser.GameObjects.Text;
  private clearedText!: Phaser.GameObjects.Text;
  private stripText!: Phaser.GameObjects.Text;
  private stripBg: Phaser.GameObjects.NineSlice | null = null;
  private hintText!: Phaser.GameObjects.Text;
  private hintBg: Phaser.GameObjects.NineSlice | null = null;
  private clockFill!: Phaser.GameObjects.NineSlice;
  private clockPx = -1;
  private clockLow = false;

  constructor() {
    super({ key: 'Labyrinth' });
  }

  init(data: LabyrinthInit): void {
    this.lang = data.lang;
    this.startRound = Math.max(1, Math.floor(data.startRound ?? 1));
    this.round = this.startRound;
    this.batteries = LABYRINTH.startBatteries;
    this.roundsCleared = 0;
    this.board = null;
    this.sentence = null;
    this.state = null;
    this.tiles = [];
    this.usedSetIds = new Set();
    this.dragging = false;
    this.lastDragCell = null;
    this.gateTimer = null;
    this.blink = null;
    this.running = false;
    this.over = false;
  }

  preload(): void {
    const bg = getBackground();
    if (bg !== 'space') {
      const asset = backgroundAsset(bg);
      if (!this.textures.exists(`bg_${asset}`)) {
        this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
      }
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestLabyrinth();
    trackLabyrinthStart();   // web demo only

    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      stopBackdropAmbience();
      this.clearTileTextures();
    });

    // A hand, not the lesson's crosshair: nothing here is aimed at, everything is tapped or
    // dragged. Same reasoning as PairsScene and MazeScene.
    this.input.setDefaultCursor('pointer');

    // Read-only use of the packs: a reward must never write to the journey.
    this.sets = usableSets(this.lang);

    this.wallLayer = this.add.graphics().setDepth(2);
    this.gateLayer = this.add.graphics().setDepth(4);
    this.trailLayer = this.add.graphics().setDepth(6);
    this.drawHud();

    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.onDown(p));
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => this.onMove(p));
    this.input.on('pointerup', () => { this.dragging = false; this.lastDragCell = null; });

    this.showHowTo(() => this.startBoard(this.round));
  }

  update(_time: number, delta: number): void {
    if (!this.running || this.over) return;
    this.msLeft -= delta;
    this.paintClock();
    if (this.msLeft <= 0) {
      this.running = false;
      this.onTimeUp();
    }
  }

  // ── the board ────────────────────────────────────────────────────────────────

  private startBoard(round: number): void {
    this.clearBoard();
    const dealt = dealBoard(round, this.sets, { lang: this.lang, exclude: this.usedSetIds });
    if (!dealt) {
      // Every rung is dealable in all ten languages (deal.test.ts), so this is a bug rather than a
      // state — but a reward run must not die on one. Forget what the run has used and re-deal.
      this.usedSetIds = new Set();
      const retry = dealBoard(round, this.sets, { lang: this.lang });
      if (!retry) { this.gameOver(); return; }
      this.board = retry.board;
      this.sentence = retry.sentence;
    } else {
      this.board = dealt.board;
      this.sentence = dealt.sentence;
    }
    for (const id of this.sentence!.setIds) this.usedSetIds.add(id);

    this.state = startWalk(this.board!, this.batteries);
    this.layoutBoard(this.board!);
    this.paintTrail();
    this.paintStrip();
    const label = i18next.t('labyrinth.round', { n: round, defaultValue: `Round ${round}` });
    this.roundText.setText(label);
    this.paintHint(round);
    // Spoken on exactly the rounds the banner is shown — one rule, one rung, read and heard.
    // This is not the §9 ban on speech: that forbids reading the WORDS of the maze, which would
    // turn a reading board into a listening test. Saying what the board wants is the rule.
    speakRoundIntro(this.hintText.text, this.lang, round);
    // Announce every rung after the first, so a new board reads as "next round" rather than as the
    // last one having been shuffled.
    if (round > this.startRound) this.floating(label, '#ffd166');
    this.msLeft = this.board!.timeMs;
    this.clockPx = -1;
    this.running = true;
  }

  /**
   * Size and place the grid.
   *
   * A cell holds a whole word, so width is what it cannot give up — the cell is sized from the
   * columns first and the rows take whatever height is left, which is the opposite of Ice Maze's
   * letter grid and the reason the ladder grows upward rather than sideways (§4.1).
   */
  private layoutBoard(board: LabyrinthBoard): void {
    this.cellW = Math.floor(PLAY.w / board.cols);
    this.cellH = Math.min(Math.floor(PLAY.h / board.rows), 132);
    this.gridX = PLAY.x + Math.floor((PLAY.w - this.cellW * board.cols) / 2);
    this.gridY = PLAY.y + Math.floor((PLAY.h - this.cellH * board.rows) / 2);

    const family = scriptFontFamily(this.lang);
    this.tiles = [];
    for (let r = 0; r < board.rows; r++) {
      const row: Tile[] = [];
      for (let c = 0; c < board.cols; c++) {
        const { x, y } = this.cellCentre(c, r);
        const plate = this.add.image(x, y, this.tileTexture(TILE_FILL)).setDepth(5);
        const label = this.add.text(x, y, board.grid[r][c], {
          fontFamily: family, fontSize: `${this.fontFor(board.grid[r][c])}px`, color: TILE_TEXT,
          align: 'center',
        }).setOrigin(0.5).setDepth(8);
        row.push({ col: c, row: r, plate, label });
      }
      this.tiles.push(row);
    }
    this.drawMaze(board);
  }

  /**
   * Shrink a word until it fits its cell, with a floor.
   *
   * The floor is why `LABYRINTH.maxWordChars` exists at all: below `minFontPx` a word stops being
   * readable, and a reading board with an unreadable cell on it is worse than no board.
   */
  private fontFor(word: string): number {
    const room = this.cellW - 22;
    const size = Math.min(30, Math.floor((room / Math.max(1, [...word].length)) * 1.85));
    return Math.max(LABYRINTH.minFontPx, size);
  }

  /**
   * The slab, the rooms, the passages, and the two ways through the outer edge.
   *
   * Drawn from the *presence* of a corridor rather than from a wall list, so the picture cannot
   * disagree with what `walk.ts` allows — the classic bug in a maze renderer is a wall painted
   * where a corridor is open, which makes the board unfair in a way no test catches.
   */
  private drawMaze(board: LabyrinthBoard): void {
    const g = this.wallLayer;
    g.clear();
    const x0 = this.gridX;
    const y0 = this.gridY;
    const w = board.cols * this.cellW;
    const h = board.rows * this.cellH;

    // The rock the maze is cut out of. Everything not carved below stays solid.
    g.fillStyle(SLAB, 1).fillRoundedRect(x0 - 10, y0 - 10, w + 20, h + 20, 18);

    // The rooms.
    for (let r = 0; r < board.rows; r++) {
      for (let c = 0; c < board.cols; c++) {
        g.fillStyle(TILE_FILL, 1).fillRoundedRect(
          x0 + c * this.cellW + 5, y0 + r * this.cellH + 5,
          this.cellW - 10, this.cellH - 10, 12,
        );
      }
    }

    // The passages: one per open corridor, spanning the gutter between two rooms.
    for (let r = 0; r < board.rows; r++) {
      for (let c = 0; c < board.cols; c++) {
        if (c + 1 < board.cols && board.open.has(edgeKey([c, r], [c + 1, r]))) {
          const pw = this.cellH * CORRIDOR_W;
          g.fillStyle(TILE_FILL, 1).fillRect(
            x0 + (c + 1) * this.cellW - 7, y0 + r * this.cellH + (this.cellH - pw) / 2, 14, pw,
          );
        }
        if (r + 1 < board.rows && board.open.has(edgeKey([c, r], [c, r + 1]))) {
          const pw = this.cellW * CORRIDOR_W;
          g.fillStyle(TILE_FILL, 1).fillRect(
            x0 + c * this.cellW + (this.cellW - pw) / 2, y0 + (r + 1) * this.cellH - 7, pw, 14,
          );
        }
      }
    }

    // The way in, cut clean through the left edge, with an arrow outside it. A child has to be able
    // to see where to put their finger without being told.
    const entryY = y0 + board.entryRow * this.cellH + this.cellH / 2;
    g.fillStyle(TILE_FILL, 1).fillRect(x0 - 12, entryY - this.cellH * CORRIDOR_W / 2, 20, this.cellH * CORRIDOR_W);
    this.marker(x0 - 40, entryY, '▶', '#8fe3a0');

    this.drawGate(board, false);
  }

  /**
   * The gate: shut is a barred door in the rock, open is a lit archway with an arrow through it.
   *
   * On its own layer so opening it costs one redraw rather than the whole maze, and so it always
   * paints ABOVE the slab. "Not clear when finished" was the other half of the play-test note — the
   * old gate was three thin lines the same colour as everything else.
   */
  private drawGate(board: LabyrinthBoard, open: boolean): void {
    const g = this.gateLayer;
    g.clear();
    const x = this.gridX + board.cols * this.cellW;
    const y = this.gridY + board.gateRow * this.cellH;
    const mid = y + this.cellH / 2;
    const mouth = this.cellH * CORRIDOR_W;

    // The passage out through the right-hand edge, always cut — what changes is whether the door
    // across it is shut.
    g.fillStyle(TILE_FILL, 1).fillRect(x - 8, mid - mouth / 2, 22, mouth);

    if (open) {
      g.fillStyle(0x6fe3a0, 1).fillRoundedRect(x + 8, mid - mouth / 2 - 4, 12, mouth + 8, 6);
      this.gateMark?.destroy();
      this.gateMark = this.marker(x + 46, mid, '▶', '#6fe3a0');
      this.tweens.add({
        targets: this.gateMark, x: '+=10', duration: 460, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    } else {
      g.fillStyle(SLAB, 1).fillRect(x + 6, mid - mouth / 2 - 6, 16, mouth + 12);
      g.lineStyle(4, 0x8d6b4a, 1);
      for (let i = 1; i <= 3; i++) {
        const yy = mid - mouth / 2 + (mouth * i) / 4;
        g.lineBetween(x - 6, yy, x + 22, yy);
      }
      this.gateMark?.destroy();
      this.gateMark = null;
    }
  }

  /** A small glyph outside the maze — the way in, and the way out once it opens. */
  private marker(x: number, y: number, glyph: string, color: string): Phaser.GameObjects.Text {
    const t = this.add.text(x, y, glyph, {
      fontFamily: FONT_STACK, fontSize: '34px', color,
    }).setOrigin(0.5).setDepth(9);
    this.markers.push(t);
    return t;
  }

  private cellCentre(col: number, row: number): { x: number; y: number } {
    return {
      x: this.gridX + col * this.cellW + this.cellW / 2,
      y: this.gridY + row * this.cellH + this.cellH / 2,
    };
  }

  /** Which cell a point is in — or the gate, which lives one column past the right-hand edge. */
  private cellAt(x: number, y: number): Cell | null {
    const board = this.board;
    if (!board) return null;
    const c = Math.floor((x - this.gridX) / this.cellW);
    const r = Math.floor((y - this.gridY) / this.cellH);
    if (r < 0 || r >= board.rows) return null;
    if (c === board.cols && r === board.gateRow && x < this.gridX + (board.cols + 1) * this.cellW) {
      return gateCell(board);
    }
    if (c < 0 || c >= board.cols) return null;
    return [c, r];
  }

  private tileTexture(fill: number): string {
    const key = `laby_${this.cellW}x${this.cellH}_${fill.toString(16)}`;
    if (!this.textures.exists(key)) {
      const g = this.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(fill, 1);
      g.fillRoundedRect(0, 0, this.cellW - 10, this.cellH - 10, 12);
      g.generateTexture(key, this.cellW - 10, this.cellH - 10);
      g.destroy();
      this.tileTexKeys.push(key);
    }
    return key;
  }

  private clearTileTextures(): void {
    for (const k of this.tileTexKeys) if (this.textures.exists(k)) this.textures.remove(k);
    this.tileTexKeys = [];
  }

  private clearBoard(): void {
    for (const row of this.tiles) for (const t of row) { t.plate.destroy(); t.label.destroy(); }
    this.cancelWalkOut();
    this.stopBlink();
    this.tiles = [];
    this.wallLayer.clear();
    this.gateLayer.clear();
    this.trailLayer.clear();
    this.gateMark?.destroy();
    this.gateMark = null;
    this.markers.forEach((m) => m.destroy());
    this.markers = [];
  }

  // ── moving ───────────────────────────────────────────────────────────────────

  private onDown(p: Phaser.Input.Pointer): void {
    if (!this.running || this.over) return;
    this.dragging = true;
    const cell = this.cellAt(p.worldX, p.worldY);
    if (cell) { this.lastDragCell = `${cell[0]},${cell[1]}`; this.tryMove(cell); }
  }

  private onMove(p: Phaser.Input.Pointer): void {
    if (!this.dragging || !this.running || this.over) return;
    const cell = this.cellAt(p.worldX, p.worldY);
    if (!cell) return;
    const k = `${cell[0]},${cell[1]}`;
    if (k === this.lastDragCell) return;
    this.lastDragCell = k;
    this.tryMove(cell);
  }

  /**
   * One move, and everything it costs.
   *
   * The charge is deliberately **quiet** (§5.3): the battery dims, and there is no sound, no snuff
   * animation and no bounce — the child is moved onto the cell they chose. Announcing the mistake
   * here would tell them the turn was wrong before they had read a word of it, and the first real
   * signal is meant to be the strip at the bottom reading gibberish.
   */
  private tryMove(to: Cell): void {
    const board = this.board;
    const state = this.state;
    if (!board || !state) return;

    const before = state.gateOpen;
    const { state: next, outcome } = move(board, state, to);
    this.state = next;

    if (outcome.kind === 'blocked') { this.bump(to); return; }
    if (outcome.kind === 'cleared') { this.completeBoard(); return; }

    if (outcome.kind === 'step' && outcome.charged) {
      this.batteries = next.batteries;
      this.paintBatteries();
      if (this.batteries <= 0) { this.paintTrail(); this.paintStrip(); this.gameOver(); return; }
    }

    this.paintTrail();
    this.paintStrip();
    if (next.gateOpen !== before) {
      this.drawGate(board, next.gateOpen);
      if (next.gateOpen) { playGateSound(); this.armWalkOut(); }
      else this.cancelWalkOut();
    }
  }

  /**
   * The open gate walks the child out by itself after `gateAutoMs`.
   *
   * The green arrow is an affordance, and an affordance is a thing a five-year-old may simply not
   * act on: play-testing found children completing the sentence and then sitting on the last cell,
   * because from where they stood the board was already solved. It IS solved — the gate opens on
   * the reading, so that last tap never asked a question and never taught anything. Making it
   * optional costs the mode nothing and removes the one way to be stuck on a board you have won.
   *
   * Still tappable, and tapping is instant: a child who does reach for the arrow should not be made
   * to wait out a timer they had already answered.
   */
  private armWalkOut(): void {
    this.cancelWalkOut();
    this.gateTimer = this.time.delayedCall(LABYRINTH.gateAutoMs, () => {
      this.gateTimer = null;
      const board = this.board;
      // Stepping back closes the gate and cancels this, but a timer that has already fired cannot
      // be un-fired — so the state is checked again here rather than trusted.
      if (!board || !this.state?.gateOpen || !this.running) return;
      this.tryMove(gateCell(board));
    });
  }

  private cancelWalkOut(): void {
    this.gateTimer?.remove(false);
    this.gateTimer = null;
  }

  /** A wall, or a cell that is not adjacent: the wall flashes and nothing else happens. Free. */
  private bump(to: Cell): void {
    playThudSound();
    const board = this.board;
    if (!board) return;
    const tile = this.tiles[to[1]]?.[to[0]];
    if (!tile) return;
    this.tweens.add({
      targets: [tile.plate, tile.label], duration: 70, yoyo: true, repeat: 1, x: '+=4',
    });
  }

  private paintTrail(): void {
    const board = this.board;
    const state = this.state;
    if (!board || !state) return;

    for (let r = 0; r < board.rows; r++) {
      for (let c = 0; c < board.cols; c++) {
        this.tiles[r][c].plate.setTexture(this.tileTexture(TILE_FILL));
      }
    }
    this.stopBlink();
    const g = this.trailLayer;
    g.clear();

    // Everything from the first cell that is NOT on the true path is wrong, including every cell
    // after it — a child who took one wrong turn and then walked three more cells has to come back
    // through all four, so all four are red.
    const onPath = new Set(board.path.map(([c, r]) => `${c},${r}`));
    const firstWrong = state.trail.findIndex(([c, r]) => !onPath.has(`${c},${r}`));

    let prev: { x: number; y: number } | null = null;
    state.trail.forEach(([c, r], i) => {
      const wrong = firstWrong !== -1 && i >= firstWrong;
      this.tiles[r][c].plate.setTexture(this.tileTexture(wrong ? WRONG_FILL : TRAIL_FILL));
      const p = this.cellCentre(c, r);
      if (prev) {
        g.lineStyle(10, wrong ? WRONG_LINE : TRAIL_LINE, wrong ? 0.95 : 0.85);
        g.lineBetween(prev.x, prev.y, p.x, p.y);
      }
      prev = p;
    });

    // The head of a wrong trail blinks: of all the red, this is the cell to step off first.
    if (firstWrong !== -1) {
      const [c, r] = state.trail[state.trail.length - 1];
      this.startBlink(this.tiles[r][c]);
    }
  }

  /**
   * Pulse the cell the child is standing on, while it is the wrong one.
   *
   * The tween is held so it can be stopped the moment the trail changes — a blink left running on a
   * tile the player has already left would be pointing at nothing, and `paintTrail` runs on every
   * single move.
   */
  private startBlink(tile: Tile): void {
    this.blink = this.tweens.add({
      targets: [tile.plate, tile.label],
      alpha: 0.35,
      duration: 320,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private stopBlink(): void {
    if (!this.blink) return;
    // Restore what the tween was driving: a stopped tween leaves its targets wherever it was, and a
    // half-faded tile would stay half-faded for the rest of the board.
    for (const t of this.blink.targets as Phaser.GameObjects.Components.Alpha[]) t.setAlpha(1);
    this.blink.remove();
    this.blink = null;
  }

  /**
   * The strip: what the child has read so far, as running text.
   *
   * Not a slot strip — no boxes, no role labels, no gaps (§4.2). Those belong to Sentence Order,
   * which hands the child the words and asks for an arrangement; naming the parts here would give
   * away both how long the sentence is and what kind of word comes next.
   */
  private paintStrip(): void {
    const board = this.board;
    const state = this.state;
    if (!board || !state) return;
    const words = strip(board, state);
    const post = board.postAfter;
    const text = words
      .map((w, i) => (post >= 0 && i === post ? `${w} ▌` : w))
      .join(' ');
    this.stripText.setText(text);
    this.stripBg?.destroy();
    this.stripBg = null;
    if (text.length > 0) this.stripBg = addRoundedLabelBg(this, this.stripText);
  }

  // ── end of a board ───────────────────────────────────────────────────────────

  private completeBoard(): void {
    this.cancelWalkOut();
    this.running = false;
    this.roundsCleared += 1;
    this.clearedText.setText(
      i18next.t('labyrinth.cleared', { n: this.roundsCleared, defaultValue: `Rounds: ${this.roundsCleared}` }),
    );

    // NO refund. A battery spent stays spent: five is the run, and the only direction it moves is
    // down.
    //
    // A perfect board used to hand one back, on the argument that it rewarded reading before
    // walking. What it actually did was blunt the only cost the mode has — a strong player took a
    // wrong turn, earned it straight back on the next board, and the wrong turn had cost them
    // nothing at all. `wasPerfect` is still what the walk reports and still worth having; it just
    // no longer buys anything.

    playRoundCompleteSound();

    // Say so, in words. "Not clear when finish" was play-test feedback on a board that ended with
    // nothing but a gate swinging and a pause: the sentence the child just assembled is the prize,
    // so it is held up in green before the next rung deals.
    const sentence = this.board?.sentence.join(' ') ?? '';
    this.floating(sentence, '#6fe3a0');

    /**
     * The next board waits for the reading to FINISH, not for a fixed pause.
     *
     * A fixed 1.7 s cut the readout off mid-sentence on the long chained boards, which is the one
     * moment in the mode where the child is meant to hear their own sentence read back properly.
     * Three gates, and all three are needed:
     *
     *  - the readout is done (or was never going to happen — muted, or no voice for the language,
     *    which `audio.speak` reports as done rather than leaving the caller waiting);
     *  - the floor has passed, so a silent device still SEES the green sentence for a beat;
     *  - the cap has not, because SpeechSynthesis does not reliably deliver `onend` (WebKit
     *    especially) and a run stuck on a board the child has already won is unforgivable.
     */
    let advanced = false;
    let spoken = false;
    let held = false;
    const advance = (): void => {
      if (advanced || this.over || !this.scene.isActive()) return;
      advanced = true;
      this.round += 1;
      this.startBoard(this.round);
    };
    const advanceWhenBothReady = (): void => { if (spoken && held) advance(); };

    // Spoken once, now that the answer is already known — the only speech in the mode (§9). During
    // the walk it would turn a reading board into a listening test.
    speakRulePrompt(sentence, this.lang, () => { spoken = true; advanceWhenBothReady(); });
    this.time.delayedCall(LABYRINTH.clearHoldMs, () => { held = true; advanceWhenBothReady(); });
    this.time.delayedCall(LABYRINTH.clearCapMs, advance);
  }

  /**
   * Time up: the way out is shown, a battery drains, and the SAME rung is re-dealt.
   *
   * Showing the answer is the only teaching a lost board does, and re-dealing the same rung rather
   * than advancing is Ice Maze's rule for the same reason — a board you did not solve has not
   * earned you the next one.
   */
  private onTimeUp(): void {
    const board = this.board;
    if (!board || this.over) return;
    // A blink left running would fight the reveal below for the same tile's alpha.
    this.stopBlink();
    // The clock can run out in the same breath as the gate opening. The armed walk-out checks
    // `running` before it fires and would bail anyway, but leaving a live timer on a board that is
    // being torn down is the kind of thing that only shows up as a bug six modes later.
    this.cancelWalkOut();

    for (const [c, r] of board.path) {
      const tile = this.tiles[r]?.[c];
      if (!tile) continue;
      tile.plate.setTexture(this.tileTexture(REVEAL_FILL));
      tile.label.setColor(REVEAL_TEXT);
    }
    this.trailLayer.clear();
    this.trailLayer.lineStyle(10, 0x8f5fd6, 0.9);
    let prev: { x: number; y: number } | null = null;
    for (const [c, r] of board.path) {
      const p = this.cellCentre(c, r);
      if (prev) this.trailLayer.lineBetween(prev.x, prev.y, p.x, p.y);
      prev = p;
    }
    this.stripText.setText(board.sentence.join(' '));
    this.stripBg?.destroy();
    this.stripBg = addRoundedLabelBg(this, this.stripText);

    this.batteries -= 1;
    this.paintBatteries();
    this.floating(i18next.t('labyrinth.timeUp', { defaultValue: 'Out of time!' }), '#ff8f8f');

    this.time.delayedCall(2400, () => {
      if (this.batteries <= 0) { this.gameOver(); return; }
      this.startBoard(this.round);
    });
  }

  private floating(text: string, color: string): void {
    const label = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, text, {
      fontFamily: FONT_STACK, fontSize: '48px', color, padding: { x: 26, y: 14 }, align: 'center',
    }).setOrigin(0.5).setDepth(100);
    const bg = addRoundedLabelBg(this, label, { radius: 20 });
    this.tweens.add({
      targets: [label, bg], y: '-=60', alpha: 0, duration: 1600, ease: 'Cubic.easeIn',
      onComplete: () => { label.destroy(); bg.destroy(); },
    });
  }

  // ── HUD ──────────────────────────────────────────────────────────────────────

  private drawBackdrop(): void {
    const bg = getBackground();
    if (bg === 'space') { new Starfield(this); return; }
    const key = `bg_${backgroundAsset(bg)}`;
    if (this.textures.exists(key)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      img.setDepth(-1);
    } else {
      new Starfield(this);
    }
  }

  private drawHud(): void {
    const back = this.drawBackButton();

    const y = back.y + back.height + 10;
    this.batteryIcons = [];
    for (let i = 0; i < LABYRINTH.startBatteries; i++) {
      this.batteryIcons.push(this.add.text(back.x + i * 38, y, BATTERY, {
        fontFamily: FONT_STACK, fontSize: '28px',
      }).setDepth(40));
    }
    this.paintBatteries();

    this.roundText = this.add.text(GAME_WIDTH - 24, 20, '', {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
    }).setOrigin(1, 0).setDepth(40);

    this.clearedText = this.add.text(GAME_WIDTH - 24, 58, '', {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
    }).setOrigin(1, 0).setDepth(40);

    this.hintText = this.add.text(GAME_WIDTH / 2, BANNER_Y, '', {
      fontFamily: FONT_STACK, fontSize: '30px', color: '#ffd166', align: 'center',
      padding: { x: 18, y: 9 }, wordWrap: { width: 820 },
    }).setOrigin(0.5).setDepth(50);

    this.stripText = this.add.text(GAME_WIDTH / 2, STRIP_Y, '', {
      fontFamily: scriptFontFamily(this.lang), fontSize: '32px', color: '#ffffff', align: 'center',
      padding: { x: 20, y: 10 }, wordWrap: { width: GAME_WIDTH - 160 },
    }).setOrigin(0.5).setDepth(50);

    const barX = GAME_WIDTH / 2 - CLOCK_W / 2;
    this.add
      .nineslice(barX, CLOCK_Y, pillTexture(this, 7, 0x1a2430, 0.75), undefined, CLOCK_W, 14, 7, 7, 7, 7)
      .setOrigin(0, 0).setDepth(40);
    this.clockFill = this.add
      .nineslice(barX, CLOCK_Y, pillTexture(this, 7, CLOCK_FILL, 0.95), undefined, 4, 14, 7, 7, 7, 7)
      .setOrigin(0, 0).setDepth(40);
  }

  /**
   * The instruction, while it is owed.
   *
   * Its pill is redrawn rather than resized because `addRoundedLabelBg` sizes the plate when it is
   * added — the same reason MazeScene redraws its ask. Cheaper than teaching the helper to resize,
   * and it happens at most three times a run.
   */
  private paintHint(round: number): void {
    this.hintBg?.destroy();
    this.hintBg = null;
    if (!speaksIntroOn(round)) { this.hintText.setText('').setVisible(false); return; }
    this.hintText.setVisible(true).setText(
      i18next.t('labyrinth.hint', { defaultValue: 'Find the correct sentences to reach the exit' }),
    );
    this.hintBg = addRoundedLabelBg(this, this.hintText);
  }

  private paintClock(): void {
    const board = this.board;
    if (!board) return;
    const frac = Phaser.Math.Clamp(this.msLeft / board.timeMs, 0, 1);
    const px = Math.max(4, Math.round(CLOCK_W * frac));
    const low = frac < 0.25;
    if (px === this.clockPx && low === this.clockLow) return;
    this.clockPx = px;
    if (low !== this.clockLow) {
      this.clockLow = low;
      this.clockFill.setTexture(pillTexture(this, 7, low ? CLOCK_LOW : CLOCK_FILL, 0.95));
    }
    this.clockFill.setSize(px, 14);
  }

  /** Batteries: spent ones dim rather than vanish, so the run's cost stays legible at a glance. */
  private paintBatteries(): void {
    this.batteryIcons.forEach((icon, i) => icon.setAlpha(i < this.batteries ? 1 : 0.22));
  }

  private drawBackButton(): Phaser.GameObjects.Text {
    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm — the decision Pairs settled. Leaving forfeits the Satellite; it was
    // spent on entry.
    back.on('pointerdown', () => this.scene.start('Menu'));
    return back;
  }

  // ── overlays ─────────────────────────────────────────────────────────────────

  private showHowTo(onStart: () => void): void {
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85,
    );
    shade.setInteractive();
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.85, GAME_HEIGHT * 0.78, 0x1a1f25,
    ).setStrokeStyle(4, 0xffd166);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 210,
      i18next.t('labyrinth.howToTitle', { defaultValue: 'Word Labyrinth' }), {
        fontFamily: FONT_STACK, fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
      }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30,
      i18next.t('labyrinth.howToBody', {
        defaultValue: 'Read your way out.\nStep on the words that make a sentence — the gate opens for the right one.\nEvery wrong turn costs a battery — read before you step.',
      }), {
        fontFamily: FONT_STACK, fontSize: '28px', color: '#ffffff', align: 'center',
        wordWrap: { width: GAME_WIDTH * 0.72 }, lineSpacing: 8,
      }).setOrigin(0.5);
    const cells = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 90,
      BATTERY.repeat(LABYRINTH.startBatteries), {
        fontFamily: FONT_STACK, fontSize: '38px',
      }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200,
      i18next.t('labyrinth.howToStart', { defaultValue: 'Tap to start' }), {
        fontFamily: FONT_STACK, fontSize: '36px', color: '#ffffff',
        backgroundColor: '#e8912d', padding: { x: 40, y: 14 }, fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, cells, start]);

    // Timestamp guard, not a delayed registration: the tap that spent the Satellite must not skip
    // the card it just opened, and a backgrounded tab must never strand the child on it.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }

  // ── end of run ───────────────────────────────────────────────────────────────

  private gameOver(): void {
    if (this.over) return;
    this.cancelWalkOut();
    this.over = true;
    this.running = false;

    trackLabyrinthEnd(this.roundsCleared);   // web demo only

    const isRecord = this.roundsCleared > this.best;
    if (isRecord) { setBestLabyrinth(this.roundsCleared); this.best = this.roundsCleared; playRecordSound(); }
    else playRoundCompleteSound();

    const headline = isRecord
      ? i18next.t('labyrinth.newBest', { defaultValue: 'New best!' })
      : i18next.t('labyrinth.gameOver', { defaultValue: 'Game Over' });
    const message = `${headline}\n${i18next.t('labyrinth.cleared', { n: this.roundsCleared, defaultValue: `Rounds: ${this.roundsCleared}` })}`;
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK, fontSize: '60px', color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 }, align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(100);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 });
    overlayBg.setAlpha(0).setScale(0.8);

    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130,
      i18next.t('labyrinth.continue', { defaultValue: 'Tap to continue' }), {
        fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
      }).setOrigin(0.5).setDepth(100);
    addRoundedLabelBg(this, cont);

    this.tweens.add({
      targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut',
    });

    const shownAt = performance.now();
    const leave = (): void => {
      if (performance.now() - shownAt < 900) return;
      this.input.off('pointerdown', leave);
      this.scene.start('Menu');
    };
    this.input.on('pointerdown', leave);

    maybeShowBuyPrompt(this, { delayMs: 1500 });
    maybeAskForRating(this, { kind: 'labyrinth', rounds: this.roundsCleared });
  }
}

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  return bg === 'fable' ? fableVariant() : bg;
}
